import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Google, LinkedIn } from "@mui/icons-material";
import AuthShell from "../components/AuthShell";
import { useAuthActions } from "../context/AuthContext";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "12px",
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(79,107,255,.14)" },
  },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthActions();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/interview-config");
    } catch (err) {
      const detail = err?.detail || err?.error || err;
      if (detail?.code === "PASSWORD_NOT_SET") {
        navigate("/set-password", { state: { email, setupToken: detail?.setup_token } });
        return;
      }
      setError(detail?.message || err?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="AI Interview Platform"
      title="Build interview confidence with focused coaching."
      subtitle="Practice live sessions, get evidence-backed feedback, and walk into interviews with a clear plan to improve."
      formTitle="Welcome back"
      formSubtitle="Sign in to continue your interview preparation."
      highlights={[
        { title: "Realistic practice", body: "Voice-based sessions tailored to your target role." },
        { title: "Clear evidence", body: "Review the moments behind every performance score." },
        { title: "Focused progress", body: "Turn each report into your next practice plan." },
      ]}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          disabled
          startIcon={<Google />}
          aria-label="Continue with Google, coming soon"
          sx={{ flex: 1, minHeight: 48, borderRadius: "12px" }}
        >
          Google
        </Button>
        <Button
          variant="outlined"
          disabled
          startIcon={<LinkedIn />}
          aria-label="Continue with LinkedIn, coming soon"
          sx={{ flex: 1, minHeight: 48, borderRadius: "12px" }}
        >
          LinkedIn
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }}>
        <Typography variant="caption" sx={{ color: "#9ca3af", px: 1 }}>or continue with email</Typography>
      </Divider>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            fullWidth
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={fieldSx}
          />
          <Box>
            <TextField
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              fullWidth
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              sx={fieldSx}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
              <Typography
                component={Link}
                to="/forgot-password"
                variant="body2"
                sx={{ color: "#3e5af5", fontWeight: 700, textDecoration: "none", p: 0.5, mr: -0.5 }}
              >
                Forgot password?
              </Typography>
            </Box>
          </Box>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ minHeight: 48, borderRadius: "12px", bgcolor: "#4f6bff", "&:hover": { bgcolor: "#3e5af5" } }}
          >
            {loading ? <CircularProgress size={20} color="inherit" aria-label="Signing in" /> : "Sign In"}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280", mt: 3 }}>
        New to Evaluate Yourself?{" "}
        <Typography component={Link} to="/register" variant="body2" sx={{ color: "#3e5af5", fontWeight: 750, textDecoration: "none" }}>
          Create account
        </Typography>
      </Typography>
    </AuthShell>
  );
}
